/**
 * src/gm/platform/errors.ts — 引擎错误类型（继承旧版语义）。
 * GmError：编排层业务错误，code 供 UI 分支，message 是玩家可读的游戏世界观文案。
 * ParseError：模型输出解析/校验失败，由重试协议消费（通常不直接给玩家看）。
 */

export type GmErrorCode =
  | 'NO_API_KEY' // 未挂载 Key
  | 'KEY_INVALID' // 401
  | 'QUOTA' // 402/429 余额不足或限流
  | 'NETWORK' // 网络断开
  | 'MISSING_DESIGNER' // 策划档案缺失
  | 'MISSING_TEAM' // 其他岗位档案缺失
  | 'BAD_STATUS' // 状态机非法迁移 / 互斥锁冲突
  | 'DESIGN_INVALID' // 策划产出重试耗尽仍不合法
  | 'ART_INVALID' // 美术产出重试耗尽仍不合法
  | 'CODE_INVALID' // 程序产出重试耗尽仍不合法（开发事故）
  | 'DEV_ACCIDENT' // 开发事故：等待玩家三选
  | 'SMOKE_ENV_BLOCKED' // 冒烟探针被 CSP/沙箱整体拦截：环境故障，不算游戏事故（防误报）
  | 'REVIEW_INVALID' // 评审违反试玩协议，重派耗尽
  | 'MISSING_ARTIFACT' // 产物区缺货
  | 'PROJECT_NOT_FOUND' // 项目不存在
  | 'STORAGE_FULL' // localStorage 超容
  | 'SAVE_CORRUPT' // 存档损坏

export class GmError extends Error {
  readonly code: GmErrorCode
  /** 附加数据（如开发事故时的残次品信息） */
  readonly detail?: unknown
  constructor(code: GmErrorCode, message: string, detail?: unknown) {
    super(message)
    this.name = 'GmError'
    this.code = code
    this.detail = detail
  }
}

export class ParseError extends Error {
  /** 校验失败项清单（人话版，用于重试反馈与耗尽后的玩家提示） */
  readonly issues: string[]
  /** 最后一次的原始输出（开发事故时供"残次品上架"兜底提取） */
  raw?: string
  constructor(message: string, issues: string[] = [message]) {
    super(message)
    this.name = 'ParseError'
    this.issues = issues
  }
}

/** HTTP 错误 → GmError 翻译（技术细节只进控制台：status 由抛点塞进 detail，不进 message） */
export function translateHttpError(status: number): GmError {
  if (status === 401) {
    return new GmError('KEY_INVALID', '线路被掐了——这把钥匙失效了，去「系统设置」换一把。')
  }
  if (status === 402) {
    return new GmError('QUOTA', '账上没钱了——去 Moonshot 控制台充值后再继续。')
  }
  if (status === 429) {
    return new GmError('QUOTA', '呼叫太频繁，员工们先去泡了杯茶。稍后再试。')
  }
  return new GmError('NETWORK', '信号丢了……检查网络后点「继续」，进度都在。')
}

/** 识别浏览器存储配额超限（localStorage setItem 抛出的 QuotaExceededError 各浏览器变体） */
export function isQuotaExceeded(err: unknown): boolean {
  const name = (err as { name?: string } | null | undefined)?.name
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED'
}
